import { type TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  AverageStat,
  Position,
} from '@sorare/core/src/__generated__/globalTypes';
import { TextSwitch } from '@sorare/core/src/atoms/inputs/TextSwitch';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { StatIcon } from '@sorare/core/src/components/stats/StatIcon';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import {
  TotalAveragePlayerScore,
  averageScoreCount,
} from '@sorare/core/src/lib/averagePlayerScore';
import { fantasy, scoutingCenter } from '@sorare/core/src/lib/glossary';
import { Stats as StatsType, statLabels } from '@sorare/core/src/lib/scoring';

import { DECISIVE_ACTIONS_BY_POSITION, STATISTICS } from 'lib/scoutingCenter';

import type { Stats_averageLastScore } from './__generated__/index.graphql';

type Props = {
  averageLastScore: Nullable<Stats_averageLastScore>;
  position: Nullable<Position>;
  mode: TotalAveragePlayerScore;
};

enum StatsDisplay {
  TOTAL = 'TOTAL',
  PER_90 = 'PER_90',
}

const options = [
  {
    label: <FormattedMessage {...fantasy.total} />,
    value: StatsDisplay.TOTAL,
  },
  {
    label: <FormattedMessage {...scoutingCenter.per90} />,
    value: StatsDisplay.PER_90,
  },
];

const Root = styled(Vertical)`
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background-color: var(--c-nd-50);
`;

const CapitalizeFirstLetter = styled(LabelL)`
  &::first-letter {
    text-transform: uppercase;
  }
`;

const Row = ({ stat, value }: { stat: AverageStat; value?: string | null }) => {
  return (
    <SBHorizontal key={stat}>
      <Horizontal>
        <StatIcon statKey={stat.toLowerCase() as StatsType} />
        {stat.toLowerCase() in statLabels && (
          <CapitalizeFirstLetter>
            <FormattedMessage
              {...statLabels[stat.toLowerCase() as StatsType]}
            />
          </CapitalizeFirstLetter>
        )}
      </Horizontal>
      <LabelL bold>{value ?? 0}</LabelL>
    </SBHorizontal>
  );
};

const excludedStats: AverageStat[] = [
  AverageStat.MINS_PLAYED,
  AverageStat.GAME_STARTED,
];

export const Stats = ({ averageLastScore, position, mode }: Props) => {
  const { formatNumber } = useIntlContext();
  const [statsDisplay, setStatsDisplay] = useState<StatsDisplay>(
    StatsDisplay.TOTAL
  );

  if (!averageLastScore || !position) return null;

  const statistics = STATISTICS;
  const decisive = DECISIVE_ACTIONS_BY_POSITION[position];

  const minutesPlayed = averageLastScore.lastIndividualStats.find(
    s => 'stat' in s && s.stat === AverageStat.MINS_PLAYED
  )?.value;

  const getStatValue = (stat: AverageStat) => {
    let value = averageLastScore.lastIndividualStats.find(
      s => 'stat' in s && s.stat === stat
    )?.value;
    if (statsDisplay === StatsDisplay.PER_90 && value && minutesPlayed) {
      if (stat === AverageStat.MINS_PLAYED && averageScoreCount[mode])
        value = minutesPlayed / averageScoreCount[mode];
      else if (!excludedStats.includes(stat))
        value = (value * 90) / minutesPlayed;
    }
    return value
      ? formatNumber(value, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })
      : value?.toString();
  };

  return (
    <Root>
      <TextSwitch
        value={statsDisplay}
        options={options}
        onChange={v => setStatsDisplay(v)}
        transparent
      />
      <Vertical>
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage {...scoutingCenter.statistics} />
        </LabelM>
        {statistics?.map(stat => {
          const value = getStatValue(stat);
          if (!value) return null;
          return <Row key={stat} stat={stat} value={value} />;
        })}

        <LabelM color="var(--c-nd-600)">
          <FormattedMessage {...scoutingCenter.decisiveActions} />
        </LabelM>
        {decisive?.map(stat => {
          const value = getStatValue(stat);
          if (value === null) return null;
          return <Row key={stat} stat={stat} value={value} />;
        })}
      </Vertical>
    </Root>
  );
};

Stats.fragments = {
  averageLastScore: gql`
    fragment Stats_averageLastScore on AverageLastScore {
      id
      lastIndividualStats {
        lastValues
        value
        ... on FootballAverageStatWithValueType {
          stat
        }
      }
    }
  ` as TypedDocumentNode<Stats_averageLastScore>,
};
